import {
  type ReactElement,
  type ReactNode,
  useDeferredValue,
  useMemo,
  useSyncExternalStore,
} from 'react';
import {
  // @ts-expect-error - this export is not declared
  __HistoryContext as HistoryContext,
  __RouterContext as RouterContext,
  type RouteComponentProps,
} from 'react-router';

import type { HybridHistory } from './hybridHistory';
import { NextLocationContext } from './useNextLocation';

export interface HybridHistoryRouterProps {
  history: HybridHistory;
  children: ReactNode;
}

// An alternative Router implementation using the new useSyncExternalStore, so it works with concurrent features in React 18
export default function HybridHistoryRouter({
  history,
  children,
}: HybridHistoryRouterProps): ReactElement {
  const nextLocation = useSyncExternalStore(
    history.listen,
    history.getLocation,
  );

  const location = useDeferredValue(nextLocation);

  const contextValue = useMemo<RouteComponentProps>(
    () => ({
      history,
      location,
      match: {
        path: '/',
        url: '/',
        params: {},
        isExact: location.pathname === '/',
      },
      staticContext: undefined,
    }),
    [history, location],
  );

  return (
    <HistoryContext.Provider value={history}>
      <RouterContext.Provider value={contextValue}>
        <NextLocationContext.Provider value={nextLocation}>
          {children}
        </NextLocationContext.Provider>
      </RouterContext.Provider>
    </HistoryContext.Provider>
  );
}
